/**
 * Internal endpoints called by integrations, never by browsers.
 * - Resend webhook: Svix signature (`svix-id`, `svix-timestamp`, `svix-signature`), replay guard on svix-id.
 * - n8n callback: HMAC signature (`X-LSC-Timestamp`, `X-LSC-Signature`), tolerance 300 s, replay guard.
 */
import { z } from 'zod';
import { SheetSyncResultSchema } from '../integrations/n8n';

/**
 * POST /api/v1/internal/resend/webhook – Resend event payload (tolerant: unknown fields and
 * event types are accepted and ignored by the domain). Only these types change state:
 * `email.delivered` → DELIVERED, `email.bounced` (bounce.type `Permanent`) → BOUNCED,
 * `email.complained` → COMPLAINED. `email.sent` → SENT (if still UNKNOWN).
 */
export const ResendWebhookEventType = {
  SENT: 'email.sent',
  DELIVERED: 'email.delivered',
  DELIVERY_DELAYED: 'email.delivery_delayed',
  BOUNCED: 'email.bounced',
  COMPLAINED: 'email.complained',
} as const;
export type ResendWebhookEventType =
  (typeof ResendWebhookEventType)[keyof typeof ResendWebhookEventType];

export const ResendWebhookPayloadSchema = z.looseObject({
  type: z.string(),
  created_at: z.string(),
  data: z.looseObject({
    /** Resend message ID – matched against registrations.email_provider_message_id. */
    email_id: z.string(),
    created_at: z.string().optional(),
    to: z.array(z.string()).optional(),
    subject: z.string().optional(),
    bounce: z
      .looseObject({
        type: z.string().optional(),
        subType: z.string().optional(),
        message: z.string().optional(),
      })
      .optional(),
  }),
});
export type ResendWebhookPayload = z.infer<typeof ResendWebhookPayloadSchema>;

export const WebhookAckResponseSchema = z.object({ received: z.literal(true) }).strict();
export type WebhookAckResponse = z.infer<typeof WebhookAckResponseSchema>;

/** POST /api/v1/internal/n8n/sheet-result – n8n reports the outcome of a sheet sync. */
export const SheetResultRequestSchema = SheetSyncResultSchema;
export type SheetResultRequest = z.infer<typeof SheetResultRequestSchema>;
export const SheetResultResponseSchema = WebhookAckResponseSchema;
